import { useState } from 'react';
import { useLocation } from 'react-router';
import { toast } from 'sonner';
import { Meta } from '@/components/common';
import { LoginForm, MfaForm } from '@/components/features/auth';
import { AuthLayout } from '@/components/layout';
import { ERRORS } from '@/constants';
import type { MfaChallenge } from '@/types';

/**
 * Signing in, both steps.
 *
 * One route rather than two, because the challenge only exists in memory: it is
 * single-use, expires in five minutes, and a `/login/mfa` URL would be a page
 * that is broken on reload, broken on a back button, and bookmarkable into a
 * dead end. Holding it in state means a reload correctly starts over.
 */
export function LoginPage() {
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const location = useLocation();

  /*
   * Where RequireAuth intercepted them, so verifying returns them there rather
   * than to the customer list. Read defensively - history state is whatever the
   * browser has, including from an older build of this app.
   */
  const state = location.state as { from?: unknown } | null;
  const from = typeof state?.from === 'string' ? state.from : undefined;

  return (
    <>
      <Meta title="Sign in" description="NuDent staff console." />

      <AuthLayout
        badge="ADMIN"
        headline="Manage every organization, seat and licence key."
        blurb="Admin access is restricted to NμDent staff accounts with hardware-backed MFA enrolled."
      >
        {challenge ? (
          <MfaForm
            challenge={challenge}
            {...(from !== undefined ? { destination: from } : {})}
            onExpired={() => {
              setChallenge(null);
              toast.error(ERRORS.mfaExpired);
            }}
            onBack={() => {
              setChallenge(null);
            }}
          />
        ) : (
          <LoginForm onChallenge={setChallenge} />
        )}
      </AuthLayout>
    </>
  );
}
